import { getDisplayValue, MultiValues } from "@/app/components/MultiValues";
import { UserFieldCellValue } from "@/app/components/UserFieldCellValue";
import { Entity, IField, ISelectFieldOption } from "@/app/fields/types";
import { CheckIcon, CloseIcon } from "@saas-ui/core";
import { ColumnDef } from "@saas-ui/react";

export const buildColumns = <T extends Entity>(
  fields: IField<T>[]
): ColumnDef<T>[] => {
  return fields
    .filter(({ is_table }) => is_table)
    .sort((a, b) => {
      const aTableOrder = a.table_order === undefined ? 1000 : a.table_order;
      const bTableOrder = b.table_order === undefined ? 1000 : b.table_order;
      return aTableOrder - bTableOrder;
    })
    .map((field) => {
      const shared = {
        header: field.header || field.label,
        accessorKey: field.field, // always required for sorting even if not used for display
      };
      switch (field.type) {
        case "select":
          return {
            ...shared,
            // disable sorting for multi fields
            enableSorting: !field.is_multi,
            cell: ({ row: { original } }) => {
              if (field.is_multi) {
                const options = (original[field.field] ||
                  []) as ISelectFieldOption[];
                if (!options.length) {
                  return "-";
                }
                return <MultiValues options={options} size="sm" limit={1} />;
              }
              const value = original[field.field] as
                | ISelectFieldOption
                | undefined;
              return (
                <span>
                  {!!value
                    ? getDisplayValue({
                        group: value.group,
                        label: value.label,
                      })
                    : "-"}
                </span>
              );
            },
          };
        case "boolean":
          return {
            ...shared,
            cell: ({ row: { original } }) => {
              const value = original[field.field] as boolean;
              return value ? (
                <div data-cy={`${String(field.field)}-true-value`}>
                  <CheckIcon />
                </div>
              ) : (
                <div data-cy={`${String(field.field)}-false-value`}>
                  <CloseIcon />
                </div>
              );
            },
          };
        case "text":
          return {
            ...shared,
            cell: ({
              row: {
                original: { [field.field]: value },
              },
            }) => {
              const final = (value as string) || "-";
              return <span>{final}</span>;
            },
          };
        case "user":
          return {
            ...shared,
            enableSorting: false,
            cell: ({
              row: {
                original: { [field.field]: value },
              },
            }) => {
              return <UserFieldCellValue id={value as string | undefined} />;
            },
          };
        case "number":
          return {
            ...shared,
            cell: ({ row: { original } }) => {
              if (field.accessor) {
                const fn = eval(field.accessor) as (row: T) => string;
                return fn(original);
              }
              const { [field.field]: value } = original;
              // todo: test null
              return <>{!!value ? value.toLocaleString() : "-"}</>;
            },
          };
        case "date":
          return {
            ...shared,
            enableSorting: false,
            cell: ({
              row: {
                original: { [field.field]: value },
              },
            }) => {
              if (!value) {
                return "-";
              }
              return (
                <>{new Date(value as string).toLocaleString().slice(0, 10)}</>
              );
            },
          };
        case "file":
          return {
            ...shared,
            enableSorting: false,
            cell: ({ row: { original } }) => {
              const fileUrl = original[field.field] as string;
              return !!fileUrl ? (
                <div data-cy={`${String(field.field)}-true-value`}>
                  <CheckIcon />
                </div>
              ) : (
                <div data-cy={`${String(field.field)}-false-value`}>
                  <CloseIcon />
                </div>
              );
            },
          };
      }
    });
};
